import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo
} from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { cn } from "@/utils/utils";
import Text, { TextVariant } from "@/components/ui/text";
import { ChevronDown, X } from "lucide-react";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import {
  EntityType,
  entityTypeToEntityName,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import SearchInput from "@/components/ui/search-input";
import { useTranslation } from "react-i18next";
import { isEmptyFieldValue } from "@/components/layouts/DetailFields";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import InlineLoader from "@/components/ui/inline-loader";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import { Check, Close } from "@dreamhub/designsystem/icons";
import { NONE_OPTION_VALUE } from "@/components/controls/Form/Form";
import { isReservedPersonId } from "@/entities/people/types";
import { debounce, find, isEmpty } from "lodash";
import ClickableLink from "@/components/ui/clickable-link";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";
import Chip from "@/components/ui/chip";
import {
  OTHER_COMPANY_OPTION_VALUE,
  OTHER_PERSON_OPTION_VALUE
} from "@/pages/leads/components/CreateLeadModal";

// "dropdown" renders identically to "form" (see hasDropdownLook) and remains
// only for the form-field call sites this PR left alone.
export type singleSelectVariant =
  | "form"
  | "dropdown"
  | "badge"
  | "inline"
  | "topFields"
  | "chip"
  // Pre-redesign name, kept so flag-off callers keep the body they had:
  // hasDefaultLook matches it explicitly, which is what base's own
  // `variant === "default"` branch did before the redesign renamed the look.
  | "default";

export interface SingleSelectOption {
  id: string | number | null;
  displayName?: string;
  name?: string;
  subtitle?: string | React.ReactNode;
  // Muted text shown inline after the name in the list, and matched by search.
  suffix?: string;
  isHidden?: boolean;
  section?: string;
  disabled?: boolean;
  comingSoon?: boolean;
}

export interface SingleSelectSection {
  id: string;
  title: string;
  order?: number;
}

export interface SingleSelectProps {
  fieldName: string;
  options?: SingleSelectOption[];
  fixedOptions?: any[];
  onSelection?: (value: number | string) => void;
  selectedValue?: number | string;
  placeholder?: string;
  formatter?: (value: number | string) => string;
  renderIcon?: (value: number | string) => JSX.Element;
  className?: string;
  popoverClassName?: string;
  popoverTriggerClassName?: string;
  entity?: EntityType;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  // Runs as the popover returns focus to the trigger on close; calling
  // preventDefault keeps focus where the host is about to put it.
  onCloseAutoFocus?: (event: Event) => void;
  showSearch?: boolean;
  treatNullAsValue?: boolean;
  variant?: singleSelectVariant;
  sideOffset?: number;
  clearable?: boolean;
  onClear?: () => void;
  disabled?: boolean;
  disabledTooltipText?: string;
  popOverHeader?: JSX.Element;
  popOverFooter?: JSX.Element | ((searchTerm: string) => JSX.Element);
  initialSearchTerm?: string;
  onSearch?: (newSearchVal: string) => void;
  isLoading?: boolean;
  selectedVariant?: TextVariant;
  triggerOnlyIcon?: JSX.Element;
  // Trigger shows only the selected option's renderIcon and an always-visible
  // chevron, not its name or subtitle (unlike triggerOnlyIcon, which adds an
  // icon beside the name).
  hideSelectedLabel?: boolean;
  filledValueClassName?: string;
  mainContainerClassName?: string;
  defaultValue?: string | number;
  triggerVariant?: TextVariant;
  customEmptyTriggerComponent?: JSX.Element;
  customTriggerComponent?: JSX.Element;
  placeholderClassName?: string;
  showSelectedItemSubtitle?: boolean;
  minItemHeight?: string;
  dropdownDirection?: "top" | "bottom" | "auto";
  showEmptyIcon?: boolean;
  hasError?: boolean;
  enableSections?: boolean;
  sections?: SingleSelectSection[];
  defaultSectionTitle?: string;
  showPlaceholderIcon?: boolean;
  hideChevron?: boolean;
  showClearButton?: boolean;
  triggerContentClassName?: string;
  readOnly?: boolean;
  chipSize?: "sm" | "lg";
}

export const SingleSelect: React.FC<SingleSelectProps> = ({
  fieldName,
  options = [],
  fixedOptions = [],
  onSelection,
  selectedValue = "",
  placeholder,
  className,
  formatter,
  renderIcon,
  popoverClassName,
  popoverTriggerClassName,
  entity,
  isOpen,
  onOpenChange,
  onCloseAutoFocus,
  showSearch = false,
  treatNullAsValue = false,
  variant = "inline",
  sideOffset = 4,
  clearable,
  onClear,
  disabled = false,
  disabledTooltipText,
  popOverHeader,
  popOverFooter,
  initialSearchTerm = "",
  onSearch,
  isLoading,
  selectedVariant,
  triggerOnlyIcon,
  hideSelectedLabel = false,
  filledValueClassName,
  mainContainerClassName,
  triggerVariant,
  customEmptyTriggerComponent,
  customTriggerComponent,
  placeholderClassName,
  showSelectedItemSubtitle = true,
  minItemHeight,
  dropdownDirection = "auto",
  showEmptyIcon = false,
  hasError = false,
  enableSections = false,
  sections = [],
  defaultSectionTitle = "",
  showPlaceholderIcon = false,
  hideChevron = false,
  showClearButton = false,
  triggerContentClassName,
  readOnly = false,
  chipSize = "lg"
}) => {
  const { t } = useTranslation();
  const isNewFieldsUI = useNewFieldsUI();

  const hasDropdownLook = variant === "form" || variant === "dropdown";
  // Dim the leading icon (via descendants) when the select is disabled. The
  // redesign uses the same token as its label so both halves match.
  const disabledIconClassName = cn(
    "contents",
    disabled && "[&_*]:!text-disabled"
  );
  const newDisabledIconClassName = cn(
    "contents",
    disabled && "[&_*]:!text-body-disabled"
  );
  const hasBadgeLook = variant === "badge";
  // "default" belongs here: base keyed this look on `variant === "default"`
  // directly, so leaving it out dropped the body and the w-full/text-left
  // trigger classes for every caller still passing the pre-redesign name —
  // CurrencySelectField's own default, and detailFields' flag-off entity select.
  const hasDefaultLook =
    variant === "inline" || variant === "topFields" || variant === "default";
  const hasChipLook = variant === "chip";

  const nonHiddenOptions = (options: any[]) =>
    options.filter((option) => !option?.isHidden);

  const isValueEmpty = isEmptyFieldValue(
    selectedValue,
    "select",
    treatNullAsValue
  );

  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();
  const [isPopoverOpen, setIsPopoverOpen] = useState(isOpen || false);
  const [isHovered, setIsHovered] = useState(false);
  const [searchValue, setSearchValue] = useState(initialSearchTerm ?? "");
  const [displayedOptions, setDisplayedOptions] = useState(
    nonHiddenOptions(options)
  );

  // Group options by section when sections are enabled
  const groupedOptions = useMemo((): Record<string, SingleSelectOption[]> => {
    if (!enableSections) {
      return { default: displayedOptions };
    }

    const groups: Record<string, SingleSelectOption[]> = {};

    displayedOptions.forEach((option: SingleSelectOption) => {
      const sectionId = option.section || "default";
      if (!groups[sectionId]) {
        groups[sectionId] = [];
      }
      groups[sectionId].push(option);
    });

    return groups;
  }, [displayedOptions, enableSections]);

  const getSectionTitle = useCallback(
    (sectionId: string) => {
      if (sectionId === "default") {
        return defaultSectionTitle;
      }
      const section = sections.find((s) => s.id === sectionId);
      return section?.title || sectionId;
    },
    [sections, defaultSectionTitle]
  );

  const orderedSectionIds = useMemo(() => {
    const sectionIds = Object.keys(groupedOptions);

    if (!enableSections) {
      return sectionIds;
    }

    return sectionIds.sort((a, b) => {
      if (a === "default") return 1;
      if (b === "default") return -1;

      const sectionA = sections.find((s) => s.id === a);
      const sectionB = sections.find((s) => s.id === b);

      const orderA = sectionA?.order ?? 999;
      const orderB = sectionB?.order ?? 999;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return a.localeCompare(b);
    });
  }, [groupedOptions, sections, enableSections]);
  const [chosenSide, setChosenSide] = useState<"top" | "bottom" | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hasIcon = Boolean(renderIcon);

  const isNoneOptionSelected = selectedValue === NONE_OPTION_VALUE;

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    if (dropdownDirection === "top") {
      setChosenSide("top");
      return;
    }

    if (dropdownDirection === "bottom") {
      setChosenSide("bottom");
      return;
    }

    // Auto mode - calculate based on available space
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    // Set the side based on available space
    setChosenSide(spaceBelow >= spaceAbove ? "bottom" : "top");
  }, [dropdownDirection]);

  useEffect(() => {
    if (isPopoverOpen) {
      calculatePosition();

      // Add resize listener to recalculate position when window size changes
      window.addEventListener("resize", calculatePosition);

      return () => {
        window.removeEventListener("resize", calculatePosition);
      };
    }
  }, [isPopoverOpen, calculatePosition]);

  const stringSelectedValue = String(selectedValue ?? "");
  const useEntityQuery = getEntityHook(entity);
  const isSpecialOtherValue =
    typeof selectedValue === "string" &&
    (selectedValue.startsWith(OTHER_COMPANY_OPTION_VALUE) ||
      selectedValue.startsWith(OTHER_PERSON_OPTION_VALUE));
  const shouldFetchEntity =
    entity && stringSelectedValue && !isSpecialOtherValue;
  const entityQuery = useEntityQuery
    ? useEntityQuery(shouldFetchEntity ? [stringSelectedValue] : [])
    : { entities: [], isSuccess: false };

  const { entities: [entityData] = [], isSuccess: isEntitySuccess } =
    entityQuery;

  useEffect(() => {
    setSearchValue(initialSearchTerm ?? "");
  }, [initialSearchTerm]);

  useEffect(() => {
    // If onSearch is provided, always update displayedOptions when options change
    // (because external search updates the options prop)
    // If no onSearch, only update when search is empty (to avoid overriding local filtering)
    if (onSearch || (searchValue ?? "").trim() === "") {
      setDisplayedOptions(nonHiddenOptions(options));
    }
  }, [options, searchValue, onSearch]);

  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const formatterRef = useRef(formatter);
  formatterRef.current = formatter;

  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      if (onSearchRef.current) {
        onSearchRef.current(searchTerm);
        return;
      }
      if (searchTerm.trim() === "") {
        setDisplayedOptions(nonHiddenOptions(optionsRef.current));
        return;
      }

      const filtered = optionsRef.current.filter((option) => {
        const searchableText = [
          option?.displayName ||
            formatterRef.current?.(option?.id as any) ||
            "",
          option?.suffix
        ]
          .filter(Boolean)
          .join(" ");
        return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
      });

      setDisplayedOptions(filtered);
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };
  const handleOpenChange = (open: boolean) => {
    if (open && showClearButton && !emptySelectedValue) return;
    setIsPopoverOpen(open);
    if (open) {
      calculatePosition();
    } else {
      setChosenSide(null);
      setSearchValue(initialSearchTerm ?? "");
      setDisplayedOptions(nonHiddenOptions(options));
    }
    onOpenChange?.(open);
  };

  // Check if entityData exists and is not already in options or fixedOptions
  const entityDataOption =
    entityData && isEntitySuccess
      ? {
          id: entityData.id,
          displayName:
            entityData.name ||
            ("displayName" in entityData ? entityData.displayName : undefined),
          name: entityData.name,
          subtitle: "subtitle" in entityData ? entityData.subtitle : undefined
        }
      : null;

  const isEntityDataInOptions =
    entityDataOption &&
    [...options, ...fixedOptions].some(
      (option) => String(option?.id) === String(entityDataOption.id)
    );

  const allOptions = [
    ...(entityDataOption && !isEntityDataInOptions ? [entityDataOption] : []),
    ...options,
    ...fixedOptions
  ];

  const selectedItem = allOptions
    ? find(allOptions, (option) => {
        if (!option) return false;
        const optionId = String(option.id);
        const selectedVal = String(selectedValue);
        return optionId === selectedVal;
      })
    : undefined;

  // Treat as empty if value is empty OR if the value is not found in the options
  const emptySelectedValue = isValueEmpty || (!isValueEmpty && !selectedItem);

  const renderOptionName = (option: any) => {
    if (option?.displayName) {
      return option?.displayName;
    }
    if (option?.name) {
      return formatter?.(option?.id);
    }
  };

  const validLink =
    entity &&
    isEntitySuccess &&
    entityData &&
    entity !== EntityType.USER &&
    !isReservedPersonId(selectedItem?.id?.toString()) &&
    selectedItem?.id !== null;

  const renderOptionItem = (
    option: any,
    isCurrent: boolean,
    isFixed: boolean = false
  ) => (
    <CommandItem
      key={`${isFixed ? "fixed" : "regular"}-${option?.id}`}
      disabled={option?.disabled}
      onSelect={() => {
        if (!isCurrent && !option?.disabled) {
          onOpenChange?.(false);
          setIsPopoverOpen((prev) => !prev);
          return onSelection && onSelection(option?.id);
        }
        return null;
      }}
      className={cn(
        "w-full cursor-pointer py-2 pl-2 pr-3 min-h-9 flex justify-between mr-3 rounded-lg transition-all duration-200 ease-in-out",
        isCurrent
          ? "font-semibold cursor-not-allowed !bg-transparent hover:!bg-transparent"
          : "hover:!bg-surface-hover-soft active:!bg-surface-pressed",
        option?.disabled &&
          "cursor-not-allowed opacity-50 hover:!bg-transparent active:!bg-transparent"
      )}
      style={minItemHeight ? { minHeight: minItemHeight } : undefined}
    >
      <div
        className={cn(
          "flex items-center",
          hasIcon ? "gap-2" : "",
          // The option's own state, not the select's — the list is only open
          // when the select is enabled, and the label below dims on the same
          // condition, so the icon has to match it.
          option?.disabled && "text-body-disabled"
        )}
      >
        {renderIcon?.(option?.id)}
        <div className="flex flex-col">
          <Text
            variant={isCurrent ? "label" : "body"}
            className={option?.disabled ? "text-body-disabled" : ""}
          >
            {renderOptionName(option)}
            {option?.suffix && (
              <Text as="span" variant="bodyTertiary" className="ml-2">
                {option.suffix}
              </Text>
            )}
          </Text>
          {option?.subtitle && (
            <>
              {typeof option.subtitle === "string" ? (
                <div className="text-sm">
                  <Text variant="smBodySecondary">{option.subtitle}</Text>
                </div>
              ) : (
                option.subtitle
              )}
            </>
          )}
        </div>
      </div>
      {isCurrent && (
        <IconContainer size="5">
          <Check />
        </IconContainer>
      )}
      {option?.comingSoon && (
        <div className="flex items-center justify-center">
          <Text variant="smBodySecondary">{t("comingSoon")}</Text>
        </div>
      )}
    </CommandItem>
  );

  const renderLegacyTriggerBody = () => (
    <div
      className={cn(
        "flex rounded transition-all duration-300 ease-in-out",
        isPopoverOpen &&
          !entity &&
          !hasDropdownLook &&
          "bg-surface-hover text-primary",
        !entity && !hasDropdownLook && !disabled && "hover:!bg-surface-hover",
        hasDropdownLook && "w-full",
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {hasDropdownLook ? (
        <div
          className={cn(
            "flex w-full justify-between pr-2 py-[6px] space-x-2 h-9 items-center rounded-lg border-[0.5px] bg-surface-field-default transition-all duration-200 ease-in-out shadow-field",
            triggerOnlyIcon ||
              (hasIcon && (!emptySelectedValue || showPlaceholderIcon))
              ? "pl-2"
              : "pl-3",
            disabled &&
              "border-border-field-disabled !bg-surface-fields-disabled !shadow-none",
            !disabled &&
              !isPopoverOpen &&
              "border-border-field-default hover:border-border-field-hover",
            isPopoverOpen &&
              "border-border-field-focus bg-surface-focus-selected shadow-field-focus",
            hasError && "!border-border-field-error",
            disabled && "text-text-disabled",

            popoverTriggerClassName,
            triggerContentClassName
          )}
        >
          {triggerOnlyIcon && (
            <span className={cn(emptySelectedValue && "pr-1")}>
              {triggerOnlyIcon}
            </span>
          )}
          {!emptySelectedValue ? (
            <div
              className={cn(
                "flex gap-2 items-center min-w-0 overflow-hidden flex-1",
                filledValueClassName
              )}
            >
              {renderIcon ? (
                <span className={disabledIconClassName}>
                  {renderIcon(selectedItem?.id)}
                </span>
              ) : null}
              {!hideSelectedLabel && (
                <Text
                  variant={selectedVariant || "body"}
                  hasTruncatedTooltip
                  className={cn(
                    "truncate block content-center flex-shrink",
                    disabled && "text-text-disabled"
                  )}
                >
                  {selectedItem?.displayName
                    ? selectedItem?.displayName
                    : formatter?.(selectedItem?.id)}
                </Text>
              )}
              {!hideSelectedLabel &&
                showSelectedItemSubtitle &&
                selectedItem?.subtitle && (
                  <Text
                    variant="smBodySecondary"
                    className="content-center truncate block flex-shrink"
                    hasTruncatedTooltip
                  >
                    {selectedItem?.subtitle}
                  </Text>
                )}
            </div>
          ) : (
            <>
              {showPlaceholderIcon && renderIcon ? (
                <span className={disabledIconClassName}>
                  {renderIcon(selectedItem?.id)}
                </span>
              ) : null}
              <Text
                variant="bodyTertiary"
                className={cn(
                  "h-full flex items-center",
                  disabled && "!text-disabled",
                  placeholderClassName
                )}
              >
                {placeholder || t("select")}
              </Text>
            </>
          )}
          {showClearButton && !emptySelectedValue && !disabled && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  asChild
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear?.();
                  }}
                  size="xs"
                  variant="iconNoBg"
                  iconClassName="[&_svg]:size-4"
                  className={cn(
                    "flex-shrink-0 transition-all duration-200 ease-in-out",
                    // handleOpenChange refuses to open the popover for a
                    // filled showClearButton field, so isPopoverOpen can
                    // never become true here — hover is this field's only
                    // reachable "focused" state, and the reveal has to key
                    // off it or the button can never show at all.
                    isHovered || isPopoverOpen
                      ? "opacity-100 pointer-events-auto"
                      : "opacity-0 w-0 pointer-events-none"
                  )}
                >
                  <span aria-hidden="true">
                    <Close />
                  </span>
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>{t("common.clear")}</TooltipContent>
            </Tooltip>
          )}
          <div className="flex items-center gap-2 flex-shrink-0">
            {clearable && !emptySelectedValue && !disabled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    asChild
                    onClick={(e) => {
                      e.stopPropagation();
                      onClear?.();
                    }}
                    size="xs"
                    variant="iconNoBg"
                    iconClassName="[&_svg]:size-4"
                    className={cn(
                      "transition-all duration-200 ease-in-out",
                      isPopoverOpen
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 w-0 pointer-events-none"
                    )}
                  >
                    <span aria-hidden="true" data-test={`${fieldName}-clear`}>
                      <X className="size-4" />
                    </span>
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>{t("common.clear")}</TooltipContent>
              </Tooltip>
            )}
            {!hideChevron && (
              <IconContainer
                color="secondary"
                size="5"
                className={cn(
                  "transition-all duration-200 rotate-0",
                  isPopoverOpen && "!text-primary rotate-180",
                  disabled && "!text-disabled"
                )}
              >
                <ChevronDown data-test={`${fieldName}-chevron`} />
              </IconContainer>
            )}
          </div>
        </div>
      ) : hasDefaultLook ? (
        <div
          className={cn(
            "p-2 rounded-lg cursor-pointer w-full",
            "flex items-center justify-between transition-all duration-200 ease-in-out",
            isPopoverOpen
              ? "border-[1px] border-primary bg-surface-focus-selected"
              : "hover:bg-surface-hover-strong border-[0.5px] border-transparent",
            disabled &&
              "hover:!bg-transparent cursor-default !opacity-100 !bg-surface-fields-disabled",
            className
          )}
        >
          {!emptySelectedValue ? (
            <div className="flex gap-2 items-center min-w-0 flex-1">
              {renderIcon?.(selectedItem?.id)}
              {hideSelectedLabel ? null : validLink ? (
                <ClickableLink
                  href={`/${entityTypeToEntityName[entity]}/${entityData.id}`}
                  onClick={() => {
                    if (entity) {
                      newOpenDrawer(entityData.id, entity);
                    }
                  }}
                  stopPropagation={true}
                >
                  <Text
                    variant={
                      triggerVariant
                        ? triggerVariant
                        : isNoneOptionSelected
                          ? "bodySecondary"
                          : "body"
                    }
                    className={cn(
                      "truncate",
                      validLink &&
                        "transition-all duration-300 ease-in-out hover:text-link"
                    )}
                  >
                    {isNoneOptionSelected
                      ? placeholder || t("select")
                      : renderOptionName(selectedItem)}
                  </Text>
                </ClickableLink>
              ) : (
                <Text
                  variant={
                    triggerVariant
                      ? triggerVariant
                      : isNoneOptionSelected
                        ? "bodySecondary"
                        : "body"
                  }
                  className="truncate"
                >
                  {isNoneOptionSelected
                    ? placeholder || t("select")
                    : renderOptionName(selectedItem)}
                </Text>
              )}
            </div>
          ) : (
            <Text
              className={cn(
                "text-base font-normal leading-5",
                disabled ? "!text-disabled" : "text-tertiary",
                placeholderClassName
              )}
            >
              {placeholder || t("select")}
            </Text>
          )}
          <div className="flex items-center gap-1 flex-shrink-0">
            {clearable && !emptySelectedValue && !disabled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    asChild
                    onClick={(e) => {
                      e.stopPropagation();
                      onClear?.();
                    }}
                    size="xs"
                    variant="iconNoBg"
                    iconClassName="[&_svg]:size-4"
                    className={cn(
                      "transition-all duration-200 ease-in-out",
                      // Reveals on isPopoverOpen only — matching the
                      // form/dropdown variants' clear button, not the
                      // chevron beside it (which still reveals on hover).
                      // Opening the popover is still reachable while this is
                      // pointer-events-none: that click lands on the trigger
                      // behind it, same as anywhere else in the closed row.
                      isPopoverOpen
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 w-0 pointer-events-none"
                    )}
                  >
                    <span aria-hidden="true" data-test={`${fieldName}-clear`}>
                      <X className="size-4" />
                    </span>
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>{t("common.clear")}</TooltipContent>
              </Tooltip>
            )}
            {!hideChevron && (
              <ChevronDown
                data-test={`${fieldName}-chevron`}
                className={cn(
                  "size-4 text-secondary transition-opacity duration-200",
                  isHovered || isPopoverOpen || (hideSelectedLabel && !disabled)
                    ? "opacity-100"
                    : "opacity-0"
                )}
              />
            )}
          </div>
        </div>
      ) : (
        !emptySelectedValue && (
          <div
            className={cn(
              "flex p-1 gap-1 text-primary h-9 items-center justify-between hover:!bg-transparent transition-all duration-200 ease-in-out",
              hasBadgeLook &&
                "p-2 min-h-fit gap-[6px] rounded-lg border-[0.5px] border-border-border3 bg-badges-default",
              hasBadgeLook &&
                !isPopoverOpen &&
                "hover:!bg-surface-hover-strong",
              isPopoverOpen && hasBadgeLook && "bg-white-4 border-primary",
              className
            )}
          >
            {renderIcon?.(selectedItem?.id)}
            {validLink ? (
              <ClickableLink
                href={`/${entityTypeToEntityName[entity]}/${entityData.id}`}
                onClick={() => {
                  if (entity) {
                    newOpenDrawer(entityData.id, entity);
                  }
                }}
                stopPropagation={true}
              >
                <Text
                  variant={
                    triggerVariant
                      ? triggerVariant
                      : isNoneOptionSelected
                        ? "bodySecondary"
                        : "body"
                  }
                  className={
                    validLink
                      ? "transition-all duration-300 ease-in-out hover:text-link"
                      : ""
                  }
                >
                  {isNoneOptionSelected
                    ? placeholder || t("select")
                    : renderOptionName(selectedItem)}
                </Text>
              </ClickableLink>
            ) : (
              <Text
                variant={
                  triggerVariant
                    ? triggerVariant
                    : isNoneOptionSelected
                      ? "bodySecondary"
                      : "body"
                }
              >
                {isNoneOptionSelected
                  ? placeholder || t("select")
                  : renderOptionName(selectedItem)}
              </Text>
            )}
          </div>
        )
      )}
      {!hasDropdownLook && !hasBadgeLook && !hasDefaultLook && (
        <>
          {showEmptyIcon && emptySelectedValue && renderIcon?.("")}
          <ChevronDown
            data-test={`${fieldName}-chevron`}
            className={cn(
              "size-4 text-secondary self-center rounded transition-all duration-200 opacity-0 overflow-hidden",
              !emptySelectedValue && "group-hover:opacity-100",
              isPopoverOpen && !emptySelectedValue && "bg-transparent",
              (isHovered || isPopoverOpen) && "opacity-100",
              ((isHovered && emptySelectedValue) ||
                (isPopoverOpen && emptySelectedValue)) &&
                "bg-surface-hover",
              entity && (isHovered || isPopoverOpen) && "bg-surface-hover",
              emptySelectedValue &&
                "group-hover:opacity-100 group-hover:bg-surface-hover"
            )}
          />
        </>
      )}
    </div>
  );

  const renderNewTriggerBody = () => (
    <div
      className={cn(
        "flex rounded transition-all duration-300 ease-in-out",
        isPopoverOpen &&
          !entity &&
          !hasDropdownLook &&
          (variant === "inline" ? "" : "bg-surface-hover text-primary"),
        hasDropdownLook && "w-full",
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {hasDropdownLook ? (
        <div
          className={cn(
            "flex w-full justify-between pr-2 py-[6px] space-x-2 h-9 items-center rounded-lg border-[0.5px] bg-surface-field-default transition-all duration-200 ease-in-out shadow-field",
            triggerOnlyIcon ||
              (hasIcon && (!emptySelectedValue || showPlaceholderIcon))
              ? "pl-2"
              : "pl-3",
            disabled &&
              "border-border-field-disabled !bg-surface-fields-disabled !shadow-none",
            !disabled &&
              !isPopoverOpen &&
              "border-border-field-default hover:border-border-field-hover",
            isPopoverOpen &&
              "border-border-field-focus bg-surface-focus-selected shadow-field-focus",
            hasError && "!border-border-field-error",
            disabled && "text-body-disabled",

            popoverTriggerClassName,
            triggerContentClassName
          )}
        >
          {triggerOnlyIcon && (
            <span className={cn(emptySelectedValue && "pr-1")}>
              {triggerOnlyIcon}
            </span>
          )}
          {!emptySelectedValue ? (
            <div
              className={cn(
                "flex gap-2 items-center min-w-0 overflow-hidden flex-1",
                filledValueClassName
              )}
            >
              <span className={newDisabledIconClassName}>
                {renderIcon?.(selectedItem?.id)}
              </span>
              {!hideSelectedLabel && (
                <Text
                  variant={selectedVariant || "body"}
                  hasTruncatedTooltip
                  className={cn(
                    "truncate block content-center flex-shrink",
                    disabled && "text-body-disabled"
                  )}
                >
                  {selectedItem?.displayName
                    ? selectedItem?.displayName
                    : formatter?.(selectedItem?.id)}
                </Text>
              )}
              {!hideSelectedLabel &&
                showSelectedItemSubtitle &&
                selectedItem?.subtitle && (
                  <Text
                    variant="smBodySecondary"
                    className="content-center truncate block flex-shrink"
                    hasTruncatedTooltip
                  >
                    {selectedItem?.subtitle}
                  </Text>
                )}
            </div>
          ) : (
            <>
              {showPlaceholderIcon && (
                <span className={newDisabledIconClassName}>
                  {renderIcon?.(selectedItem?.id)}
                </span>
              )}
              <Text
                variant="bodyTertiary"
                className={cn(
                  "h-full flex items-center",
                  disabled && "!text-body-disabled",
                  placeholderClassName
                )}
              >
                {placeholder || t("select")}
              </Text>
            </>
          )}
          <div className="flex items-center gap-1 flex-shrink-0">
            {(clearable || showClearButton) &&
              !emptySelectedValue &&
              !disabled && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton
                      asChild
                      onClick={(e) => {
                        e.stopPropagation();
                        onClear?.();
                      }}
                      size="xs"
                      variant="iconNoBg"
                      iconClassName="[&_svg]:size-4"
                      className={cn(
                        "transition-all duration-200 ease-in-out",
                        // handleOpenChange refuses to open a filled
                        // showClearButton field, so hover is its only reachable
                        // "focused" state and the reveal must key off it.
                        isPopoverOpen || (showClearButton && isHovered)
                          ? "opacity-100 pointer-events-auto"
                          : "opacity-0 w-0 pointer-events-none"
                      )}
                    >
                      <span aria-hidden="true" data-test={`${fieldName}-clear`}>
                        <X className="size-4" />
                      </span>
                    </IconButton>
                  </TooltipTrigger>
                  <TooltipContent>{t("common.clear")}</TooltipContent>
                </Tooltip>
              )}
            {!hideChevron && (
              <IconContainer
                color="secondary"
                size="5"
                className={cn(
                  "transition-all duration-200 rotate-0",
                  isPopoverOpen && "!text-primary rotate-180",
                  disabled && "!text-body-disabled"
                )}
              >
                <ChevronDown
                  data-test={`${fieldName}-chevron`}
                  size={20}
                  strokeWidth={1.5}
                />
              </IconContainer>
            )}
          </div>
        </div>
      ) : hasDefaultLook ? (
        <div
          className={cn(
            "px-2 py-1 min-h-9 rounded-lg w-full",
            "flex items-center justify-between transition-all duration-200 ease-in-out",
            isPopoverOpen
              ? "border-[0.5px] border-border-field-focus bg-surface-focus-selected"
              : cn(
                  "border-[0.5px] border-transparent",
                  variant === "inline" &&
                    !disabled &&
                    "hover:bg-surface-hover-soft"
                ),
            disabled && variant === "inline"
              ? "cursor-default"
              : "cursor-pointer",
            disabled &&
              variant !== "inline" &&
              "hover:!bg-transparent cursor-not-allowed !opacity-100 !bg-surface-fields-disabled",
            className
          )}
        >
          {!emptySelectedValue ? (
            <div className="flex gap-2 items-center min-w-0 flex-1">
              <span className={newDisabledIconClassName}>
                {renderIcon?.(selectedItem?.id)}
              </span>
              {hideSelectedLabel ? null : validLink ? (
                <ClickableLink
                  href={`/${entityTypeToEntityName[entity]}/${entityData.id}`}
                  onClick={() => {
                    if (entity) {
                      newOpenDrawer(entityData.id, entity);
                    }
                  }}
                  stopPropagation={true}
                >
                  <Text
                    variant={
                      triggerVariant
                        ? triggerVariant
                        : isNoneOptionSelected
                          ? "bodySecondary"
                          : "body"
                    }
                    className={cn(
                      "truncate",
                      validLink &&
                        "transition-all duration-300 ease-in-out hover:text-link"
                    )}
                  >
                    {isNoneOptionSelected
                      ? placeholder || t("select")
                      : renderOptionName(selectedItem)}
                  </Text>
                </ClickableLink>
              ) : (
                <Text
                  variant={
                    triggerVariant
                      ? triggerVariant
                      : isNoneOptionSelected
                        ? "bodySecondary"
                        : "body"
                  }
                  className={cn("truncate", disabled && "!text-body-disabled")}
                >
                  {isNoneOptionSelected
                    ? placeholder || t("select")
                    : renderOptionName(selectedItem)}
                </Text>
              )}
            </div>
          ) : (
            <Text
              variant="bodyTertiary"
              className={cn(
                disabled && "!text-body-disabled",
                placeholderClassName
              )}
            >
              {placeholder || t("select")}
            </Text>
          )}
          <div className="flex items-center gap-1 flex-shrink-0">
            {clearable && !emptySelectedValue && !disabled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    asChild
                    onClick={(e) => {
                      e.stopPropagation();
                      onClear?.();
                    }}
                    size="xs"
                    variant="iconNoBg"
                    iconClassName="[&_svg]:size-4"
                    className={cn(
                      "transition-all duration-200 ease-in-out",
                      // Reveals on isPopoverOpen only — matching the
                      // form/dropdown variants' clear button, not the
                      // chevron beside it (which still reveals on hover).
                      // Opening the popover is still reachable while this is
                      // pointer-events-none: that click lands on the trigger
                      // behind it, same as anywhere else in the closed row.
                      isPopoverOpen
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 w-0 pointer-events-none"
                    )}
                  >
                    <span aria-hidden="true" data-test={`${fieldName}-clear`}>
                      <X className="size-4" />
                    </span>
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>{t("common.clear")}</TooltipContent>
              </Tooltip>
            )}
            {!hideChevron && !disabled && (
              <IconContainer
                color="tertiary"
                size="5"
                className={cn(
                  "flex-shrink-0 transition-all duration-200 rotate-0",
                  isHovered || isPopoverOpen || hideSelectedLabel
                    ? "opacity-100"
                    : "opacity-0",
                  isPopoverOpen && "!text-primary rotate-180"
                )}
              >
                <ChevronDown
                  data-test={`${fieldName}-chevron`}
                  size={20}
                  strokeWidth={1.5}
                />
              </IconContainer>
            )}
          </div>
        </div>
      ) : (
        !emptySelectedValue && (
          <div
            className={cn(
              "flex p-1 gap-1 text-primary h-9 items-center justify-between hover:!bg-transparent transition-all duration-200 ease-in-out",
              hasBadgeLook &&
                "p-2 min-h-fit gap-[6px] rounded-lg border-[0.5px] border-border-border3 bg-badges-default",
              hasBadgeLook &&
                !isPopoverOpen &&
                "hover:!bg-surface-hover-strong",
              isPopoverOpen && hasBadgeLook && "bg-white-4 border-primary",
              className
            )}
          >
            {renderIcon?.(selectedItem?.id)}
            {validLink ? (
              <ClickableLink
                href={`/${entityTypeToEntityName[entity]}/${entityData.id}`}
                onClick={() => {
                  if (entity) {
                    newOpenDrawer(entityData.id, entity);
                  }
                }}
                stopPropagation={true}
              >
                <Text
                  variant={
                    triggerVariant
                      ? triggerVariant
                      : isNoneOptionSelected
                        ? "bodySecondary"
                        : "body"
                  }
                  className={
                    validLink
                      ? "transition-all duration-300 ease-in-out hover:text-link"
                      : ""
                  }
                >
                  {isNoneOptionSelected
                    ? placeholder || t("select")
                    : renderOptionName(selectedItem)}
                </Text>
              </ClickableLink>
            ) : (
              <Text
                variant={
                  triggerVariant
                    ? triggerVariant
                    : isNoneOptionSelected
                      ? "bodySecondary"
                      : "body"
                }
              >
                {isNoneOptionSelected
                  ? placeholder || t("select")
                  : renderOptionName(selectedItem)}
              </Text>
            )}
          </div>
        )
      )}
      {!hasDropdownLook && !hasBadgeLook && !hasDefaultLook && (
        <>
          {showEmptyIcon && emptySelectedValue && renderIcon?.("")}
          <ChevronDown
            strokeWidth={1.5}
            className={cn(
              "size-4 text-secondary self-center rounded transition-all duration-200 opacity-0 overflow-hidden",
              !emptySelectedValue && "group-hover:opacity-100",
              isPopoverOpen && !emptySelectedValue && "bg-transparent",
              (isHovered || isPopoverOpen) && "opacity-100",
              ((isHovered && emptySelectedValue) ||
                (isPopoverOpen && emptySelectedValue)) &&
                "bg-surface-hover",
              entity && (isHovered || isPopoverOpen) && "bg-surface-hover",
              emptySelectedValue &&
                "group-hover:opacity-100 group-hover:bg-surface-hover",
              isPopoverOpen && "rotate-180"
            )}
          />
        </>
      )}
    </div>
  );

  const renderTrigger = () =>
    hasChipLook ? (
      <Chip
        size={chipSize}
        icon={
          triggerOnlyIcon ??
          (hasIcon ? renderIcon?.(selectedItem?.id) : undefined)
        }
        label={
          emptySelectedValue
            ? placeholder || t("select")
            : (selectedItem?.displayName ?? formatter?.(selectedItem?.id))
        }
        filled={!emptySelectedValue}
        ghost={emptySelectedValue}
        selected={isPopoverOpen}
        disabled={disabled}
        removable={!emptySelectedValue && !disabled && Boolean(onClear)}
        onRemove={() => onClear?.()}
        textClassName={filledValueClassName}
      />
    ) : isNewFieldsUI ? (
      renderNewTriggerBody()
    ) : (
      renderLegacyTriggerBody()
    );

  const renderLoading = () => (
    <div className="w-full h-full items-center justify-center bg-surface-elevation2">
      <InlineLoader label={t("loading")} />
    </div>
  );

  if (readOnly) {
    // Same resolution the list uses: displayName first, then formatter for an
    // option carrying only a name. Inverting it dropped the name-only case.
    // Falls back the same way the interactive trigger does, so an unset
    // read-only field reads as empty rather than rendering a blank row.
    const optionLabel = selectedItem ? renderOptionName(selectedItem) : undefined;
    const displayValue = (optionLabel ?? placeholder) || t("select");
    return (
      <div className="px-2 py-2 min-h-9 flex items-center">
        {/* Only a resolved option label is a real value; every other branch
            above lands on placeholder text, which carries the placeholder look. */}
        <Text variant={optionLabel ? "body" : "bodyTertiary"}>{displayValue}</Text>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg",
        (emptySelectedValue || hasDefaultLook) && !hasChipLook && "w-full",
        hasChipLook && "w-fit",
        mainContainerClassName
      )}
    >
      <Popover
        open={isOpen !== undefined ? isOpen : isPopoverOpen}
        onOpenChange={handleOpenChange}
        modal
      >
        <PopoverTrigger
          ref={triggerRef}
          className={cn(
            "pl-1 group min-h-9",
            hasDropdownLook && "p-0 w-full",
            hasDefaultLook && "p-0 w-full",
            hasChipLook && "p-0 w-fit flex items-center",
            (hasDropdownLook || hasDefaultLook) && "text-left",
            !hasChipLook && popoverTriggerClassName
          )}
          disabled={disabled}
          // A label-less trigger has no visible text to name it: use the label
          // it hides, then the option's name.
          aria-label={
            hideSelectedLabel && selectedItem
              ? selectedItem.displayName ||
                formatter?.(selectedItem.id) ||
                selectedItem.name
              : undefined
          }
          data-test={`${fieldName}-trigger`}
          onKeyDown={(keyEvent) => {
            // Same guard as the sibling triggers: the clear control lives
            // inside this trigger, so its own keys must not reach here.
            if (keyEvent.target !== keyEvent.currentTarget) return;
            const popoverOpen = isOpen !== undefined ? isOpen : isPopoverOpen;
            if (
              (keyEvent.key === "Delete" || keyEvent.key === "Backspace") &&
              !popoverOpen &&
              (clearable || showClearButton) &&
              !emptySelectedValue &&
              !disabled
            ) {
              keyEvent.preventDefault();
              onClear?.();
            }
          }}
        >
          {disabled && disabledTooltipText ? (
            <Tooltip>
              <TooltipTrigger asChild>{renderTrigger()}</TooltipTrigger>
              <TooltipContent>{disabledTooltipText}</TooltipContent>
            </Tooltip>
          ) : customTriggerComponent ? (
            customTriggerComponent
          ) : emptySelectedValue && customEmptyTriggerComponent ? (
            customEmptyTriggerComponent
          ) : (
            renderTrigger()
          )}
        </PopoverTrigger>
        <PopoverContent
          data-test={`${fieldName}-options`}
          padding={4}
          className={cn(
            "flex flex-col",
            hasDropdownLook || hasDefaultLook
              ? "w-[--radix-popper-anchor-width] min-w-[240px]"
              : "max-w-[15vw]",
            popoverClassName
          )}
          align="start"
          side={chosenSide || undefined}
          avoidCollisions={true}
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          sideOffset={sideOffset}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={onCloseAutoFocus}
          // In modal mode Radix treats a pointerdown on the trigger as outside
          // and closes first, collapsing the clear button out from under its
          // own click so it never reaches onClear. Excluding the trigger is
          // what Radix's own non-modal path does.
          onPointerDownOutside={(e) => {
            if (triggerRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          {popOverHeader && popOverHeader}
          <Command className="w-full">
            <div className="flex flex-col gap-2 max-h-[300px]">
              {(onSearch || showSearch) && (
                <div className="sticky top-0 bg-surface-elevation2 z-10">
                  <SearchInput
                    placeholder={t("search")}
                    value={searchValue}
                    onChange={handleSearchChange}
                    autoFocus
                  />
                </div>
              )}
              <CommandList className="overflow-y-auto">
                {!isEmpty(fixedOptions) && (
                  <CommandGroup
                    className={cn(
                      "pb-1",
                      !isEmpty(displayedOptions) &&
                        "border-b border-border-border2"
                    )}
                  >
                    {fixedOptions.map((option) => {
                      const isCurrent =
                        String(selectedItem?.id) === String(option?.id);
                      return renderOptionItem(option, isCurrent, true);
                    })}
                  </CommandGroup>
                )}
                {isLoading ? (
                  <CommandGroup>{renderLoading()}</CommandGroup>
                ) : enableSections ? (
                  orderedSectionIds.map((sectionId) => {
                    const sectionOptions = groupedOptions[sectionId];
                    const sectionTitle = getSectionTitle(sectionId);

                    return (
                      <CommandGroup
                        key={sectionId}
                        heading={sectionTitle}
                        className="[&_[cmdk-group-heading]]:font-normal [&_[cmdk-group-heading]]:text-secondary"
                      >
                        {sectionOptions.map((option) => {
                          const isCurrent =
                            String(selectedItem?.id) === String(option?.id);
                          return renderOptionItem(option, isCurrent);
                        })}
                      </CommandGroup>
                    );
                  })
                ) : (
                  <CommandGroup>
                    {displayedOptions.length > 0 ? (
                      displayedOptions.map((option) => {
                        const isCurrent =
                          String(selectedItem?.id) === String(option?.id);
                        return renderOptionItem(option, isCurrent);
                      })
                    ) : (
                      <div className="px-2 py-4 text-center">
                        <Text variant="bodyTertiary">
                          {t("common.noResults", "No results found")}
                        </Text>
                      </div>
                    )}
                  </CommandGroup>
                )}
              </CommandList>
            </div>
          </Command>
          {popOverFooter &&
            (typeof popOverFooter === "function"
              ? popOverFooter(searchValue)
              : popOverFooter)}
        </PopoverContent>
      </Popover>
      {entity && (
        <NewQuickView
          isOpen={newDrawerState.isOpen}
          entityId={newDrawerState.entityId}
          entityType={entity}
          onClose={newCloseDrawer}
        />
      )}
    </div>
  );
};

export default SingleSelect;
